"use client"

import React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Handle, Position, useNodes, useEdges } from "reactflow"
import { router } from "@inertiajs/react"
import { Pencil, Minus, Plus, Trash2 } from "lucide-react"
import "./Node.css"
import "./UpdateNoteNode.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import CopyPasteButtons from "./CopyPasteButtons"
import EnhancedVariableReference from "./EnhancedVariableReference"
import { collectUpstreamFormFieldVariables, hasUpstreamTaskNode } from "@/lib/workflowFormFieldVariables"
import { getSystemVariablesForContext } from "@/lib/workflowSystemVariables"

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))
const getSettingValue = (source, key, fallback = "") => {
  if (source?.[key] !== undefined && source?.[key] !== null) {
    return ensureString(source[key])
  }
  return ensureString(fallback)
}

const OPERATIONS = [
  { value: "replace_text",            label: "Replace text" },
  { value: "append",                  label: "Append to text" },
  { value: "update_category",         label: "Update category only" },
  { value: "update_text_and_category", label: "Update text + category" },
]

const OPERATION_BLURBS = {
  replace_text:             "Replaces the existing note text with new text.",
  append:                   "Appends a stamped line to the existing note (e.g. \"hello world by DME A/R Workflow\").",
  update_category:          "Changes only the note's category. Leaves the text alone.",
  update_text_and_category: "Updates both the text and the category.",
}

const NOTE_CATEGORY_ENDPOINT = "https://api.integrityprodserver.com/intserver-api/lp/getNoteCategory.php"
const DEFAULT_NOTE_CATEGORIES = [
  { nct_id: "1", note_category: "General" },
  { nct_id: "2", note_category: "Installer App" },
  { nct_id: "3", note_category: "Product Description" },
  { nct_id: "4", note_category: "Technician Debrief" },
]

const requiresNoteId = (operation) =>
  operation === "append" || operation === "update_category"

const requiresNoteText = (operation) =>
  operation === "replace_text" || operation === "append" || operation === "update_text_and_category"

const requiresCategory = (operation) =>
  operation === "update_category" || operation === "update_text_and_category"

const VAR_HINT = "Tip: use {{variableName}} to pull values from previous steps."

const UpdateNoteNode = ({ data, id, databaseId, csrf_token }) => {
  const { currentWorkflow, updateNode } = useWorkflow()
  const { expanded = true } = data
  const settings = data?.settings || {}
  const resolvedWorkflowId = currentWorkflow?.id ?? data?.workflowId ?? null
  const stepDatabaseId =
    databaseId ??
    data?.databaseId ??
    (typeof id === "string" && id.startsWith("step-") ? id.replace("step-", "") : null)

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_NOTE_CATEGORIES)
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)
  const [categoryLoadError, setCategoryLoadError] = useState("")
  const nodes = useNodes()
  const edges = useEdges()

  const buildState = (src, srcSettings) => ({
    label: ensureString(src.label) || "Update Note",
    name: ensureString(src.name) || ensureString(src.label) || "Update Note",
    description: ensureString(src.description),
    operation: ensureString(src.operation) || getSettingValue(srcSettings, "operation", "append") || "append",
    mode: ensureString(src.mode) || getSettingValue(srcSettings, "mode", "job") || "job",
    recid: ensureString(src.recid) || getSettingValue(srcSettings, "recid", ""),
    noteId: ensureString(src.noteId) || getSettingValue(srcSettings, "note_id", ""),
    noteText: ensureString(src.noteText) || getSettingValue(srcSettings, "note_notes", ""),
    categorySource: ensureString(src.categorySource) || getSettingValue(srcSettings, "category_source", "label") || "label",
    noteCategory: ensureString(src.noteCategory) || getSettingValue(srcSettings, "note_category", ""),
    nctId: ensureString(src.nctId) || getSettingValue(srcSettings, "nct_id", ""),
    appendBy: ensureString(src.appendBy) || getSettingValue(srcSettings, "append_by", "DME A/R Workflow") || "DME A/R Workflow",
    empId: ensureString(src.empId) || getSettingValue(srcSettings, "emp_id", ""),
    noteImportant:
      typeof src.noteImportant === "boolean"
        ? src.noteImportant
        : Boolean(srcSettings?.note_important),
    expanded: src.expanded ?? true,
  })

  const [nodeData, setNodeData] = useState(() => buildState(data, settings))
  const nodeDataRef = useRef(nodeData)

  useEffect(() => {
    setNodeData(buildState(data, data?.settings || {}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.label, data.name, data.description, data.operation, data.mode, data.recid,
    data.noteId, data.noteText, data.categorySource, data.noteCategory, data.nctId,
    data.appendBy, data.empId, data.noteImportant, data.expanded, data.settings, id,
  ])

  useEffect(() => {
    nodeDataRef.current = nodeData
  }, [nodeData])

  useEffect(() => {
    const abortController = new AbortController()

    const loadCategories = async () => {
      setIsLoadingCategories(true)
      setCategoryLoadError("")

      try {
        const response = await fetch(NOTE_CATEGORY_ENDPOINT, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`Category request failed with status ${response.status}`)
        }

        const payload = await response.json()
        const fetchedCategories = Array.isArray(payload?.categories) ? payload.categories : []
        const normalized = fetchedCategories
          .map((item) => ({
            nct_id: ensureString(item?.nct_id),
            note_category: ensureString(item?.note_category),
          }))
          .filter((item) => item.note_category !== "")

        if (normalized.length > 0) {
          setCategoryOptions(normalized)
        } else {
          setCategoryLoadError("No note categories returned from LP API. Using defaults.")
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to load LP note categories:", error)
          setCategoryLoadError("Unable to load categories from LP API. Using defaults.")
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingCategories(false)
        }
      }
    }

    loadCategories()

    return () => {
      abortController.abort()
    }
  }, [])

  const updateWorkflowContext = useCallback((updatedData) => {
    if (currentWorkflow) {
      updateNode(currentWorkflow.id, id, { data: updatedData })
    }
  }, [currentWorkflow, id, updateNode])

  useEffect(() => {
    // Keep nctId in sync with the selected label category so backend can prefer
    // numeric category IDs (required for custom labels like "A/R Workflow").
    if (nodeData.categorySource === "nct_id") return
    if (!nodeData.noteCategory || nodeData.nctId) return
    if (!Array.isArray(categoryOptions) || categoryOptions.length === 0) return

    const matched = categoryOptions.find((c) => c.note_category === nodeData.noteCategory)
    const resolvedNctId = ensureString(matched?.nct_id)
    if (!resolvedNctId) return

    const updated = {
      ...nodeDataRef.current,
      nctId: resolvedNctId,
    }

    setNodeData(updated)
    updateWorkflowContext(updated)
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, { ...updated, expanded })
    }
  }, [
    categoryOptions,
    nodeData.categorySource,
    nodeData.noteCategory,
    nodeData.nctId,
    updateWorkflowContext,
    data,
    id,
    expanded,
  ])

  const handleChange = (field, value, options = {}) => {
    const { persistOnChange = false } = options
    const updated = {
      ...nodeDataRef.current,
      [field]: typeof value === "boolean" ? value : ensureString(value),
    }
    if (field === "name") {
      updated.label = ensureString(value) || "Update Note"
    }
    setNodeData(updated)
    if (persistOnChange) {
      updateWorkflowContext(updated)
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...updated, expanded })
      }
    }
  }

  const handleCategorySelectChange = (selectedCategory) => {
    const matched = categoryOptions.find((c) => c.note_category === selectedCategory)
    const normalizedCategory = ensureString(selectedCategory)
    const updated = {
      ...nodeDataRef.current,
      noteCategory: normalizedCategory,
      nctId: normalizedCategory ? ensureString(matched?.nct_id || "") : "",
    }
    setNodeData(updated)
    updateWorkflowContext(updated)
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, { ...updated, expanded })
    }
  }

  const handleBlur = () => {
    const nodeId = id
    const persist = data.onNodeDataChange
    setTimeout(() => {
      const current = nodeDataRef.current
      if (!current) return
      updateWorkflowContext(current)
      if (persist) {
        persist(nodeId, { ...current, expanded })
      }
    }, 0)
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest(".node-content")
      if (!nodeContent) return
      if (!e.target.closest(".update-note-node-outline")) return
      if (e.ctrlKey || e.shiftKey) return
      e.stopPropagation()
    }
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  const inputClass =
    "w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"

  const getAvailableRequestVariables = () => {
    if (!nodes || !edges) return []
    const availableVariables = []
    const visitedNodes = new Set()

    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)

      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return

      if (
        (node.type === "requestNode" || node.type === "getNoteNode") &&
        Array.isArray(node.data?.extractedVariables)
      ) {
        availableVariables.push(...node.data.extractedVariables)
      }

      const incomingEdges = edges.filter((edge) => edge.target === nodeId)
      incomingEdges.forEach((edge) => findUpstreamRequestNodes(edge.source))
    }

    findUpstreamRequestNodes(id)
    return availableVariables.filter((variable) => variable && typeof variable.name === "string")
  }

  const getAvailableFormFields = () => collectUpstreamFormFieldVariables(nodes, edges, id)

  const getSystemVariables = () =>
    getSystemVariablesForContext({ hasUpstreamCompletedTask: hasUpstreamTaskNode(nodes, edges, id) })

  const getAvailableVariables = () => {
    const requestVariables = getAvailableRequestVariables()
    const formVariables = getAvailableFormFields()
    return [...requestVariables, ...formVariables]
  }

  const showNoteId = requiresNoteId(nodeData.operation)
  const showNoteText = requiresNoteText(nodeData.operation)
  const showCategory = true
  const showAppendFields = nodeData.operation === "append"
  const hasNoteId = ensureString(nodeData.noteId).trim() !== ""
  const hasCategory = ensureString(nodeData.noteCategory).trim() !== ""
  const noteIdIsRequired = showCategory && !hasCategory
  const categoryIsRequired = showCategory && !hasNoteId

  return (
    <div
      className={`trigger-node-container update-note-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "update-note-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <Pencil size={16} className="trigger-icon" />
          <span>{nodeData.name && nodeData.name.trim() !== "" ? nodeData.name : "Update Note"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons
            nodeId={id}
            nodeType="updateNoteNode"
            nodeName={nodeData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm("Are you sure you want to delete this node?")) {
                try {
                  if (!resolvedWorkflowId || !stepDatabaseId) {
                    throw new Error("Unable to delete node: missing workflow or step database ID")
                  }
                  router.delete(`/workflows/${resolvedWorkflowId}/steps/${stepDatabaseId}`, {
                    preserveState: true,
                    preserveScroll: true,
                    only: [],
                    onSuccess: () => {
                      if (typeof data.onDeleteNode === "function") {
                        data.onDeleteNode(id)
                      }
                    },
                    onError: (errors) => {
                      console.error("Failed to delete update note node via Inertia:", errors)
                      alert("Failed to delete node. Please try again.")
                    },
                  })
                } catch (error) {
                  console.error("Failed to delete node:", error)
                  alert("Failed to delete node. Please try again.")
                }
              }
            }}
            title="Delete node"
            style={{ color: "#ef4444" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="node-content nowheel dark:bg-card dark:text-white dark:border-border"
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="update-note-info-banner">
            <strong>Update Note</strong> &mdash; calls LP&apos;s <code>saveNote.php</code> to update an existing note in <code>not_notes</code>.
            <div style={{ marginTop: 4 }}>{OPERATION_BLURBS[nodeData.operation]}</div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">Node Name</label>
            <EnhancedVariableReference
              value={nodeData.name}
              onChange={(value) => handleChange("name", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="Update Note"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">Description</label>
            <EnhancedVariableReference
              value={nodeData.description}
              onChange={(value) => handleChange("description", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="Append a status line to the LP note"
              className={inputClass}
              showDateVariables={true}
            />
          </div>

          <div className="node-field">
            <label className="dark:text-white">Operation</label>
            <select
              value={nodeData.operation}
              onChange={(e) => handleChange("operation", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className={inputClass}
            >
              {OPERATIONS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Mode<span className="update-note-required">*</span>
            </label>
            <select
              value={nodeData.mode}
              onChange={(e) => handleChange("mode", e.target.value, { persistOnChange: true })}
              onBlur={handleBlur}
              className={inputClass}
            >
              <option value="job">Job (job note)</option>
              <option value="cst">Customer (cst note)</option>
            </select>
            <div className="update-note-field-hint">
              Picks which not_notes record set to update. Most workflows use <strong>job</strong>.
            </div>
          </div>

          <div className="node-field">
            <label className="dark:text-white">
              Record ID (recid)<span className="update-note-required">*</span>
            </label>
            <EnhancedVariableReference
              value={nodeData.recid}
              onChange={(value) => handleChange("recid", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder=""
              className={inputClass}
              showDateVariables={true}
            />
            <div className="update-note-field-hint">{VAR_HINT}</div>
          </div>

          {showNoteId && (
            <div className="node-field">
              <label className="dark:text-white">
                Note ID{noteIdIsRequired ? <span className="update-note-required">*</span> : null}
              </label>
              <EnhancedVariableReference
                value={nodeData.noteId}
                onChange={(value) => handleChange("noteId", value)}
                onBlur={handleBlur}
                availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
                placeholder=""
                className={inputClass}
                showDateVariables={true}
              />
              <div className="update-note-field-hint">
                {showCategory
                  ? "Provide Note ID or Category. If one is filled, the other is optional."
                  : "The not_notes.id of the row to update."}{" "}
                {VAR_HINT}
              </div>
            </div>
          )}

          {!showNoteId && nodeData.operation === "update_text_and_category" && (
            <div className="node-field">
              <label className="dark:text-white">
                Note ID{noteIdIsRequired ? <span className="update-note-required">*</span> : null}
              </label>
              <EnhancedVariableReference
                value={nodeData.noteId}
                onChange={(value) => handleChange("noteId", value)}
                onBlur={handleBlur}
                availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
                placeholder=""
                className={inputClass}
                showDateVariables={true}
              />
              <div className="update-note-field-hint">
                Provide Note ID or Category. If one is filled, the other is optional. {VAR_HINT}
              </div>
            </div>
          )}

          {showNoteText && (
            <div className="node-field">
              <label className="dark:text-white">
                Note Text<span className="update-note-required">*</span>
              </label>
              <EnhancedVariableReference
                value={nodeData.noteText}
                onChange={(value) => handleChange("noteText", value)}
                onBlur={handleBlur}
                availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
                multiline={true}
                rows={3}
                placeholder={
                  nodeData.operation === "append"
                    ? "Text to append (e.g. \"Status update sent\")"
                    : "New note text"
                }
                className={inputClass}
                showDateVariables={true}
              />
              <div className="update-note-field-hint">{VAR_HINT}</div>
            </div>
          )}

          {showCategory && (
            <div className="node-field">
              <label className="dark:text-white">
                Category{categoryIsRequired ? <span className="update-note-required">*</span> : null}
              </label>
              <select
                value={nodeData.noteCategory}
                onChange={(e) => handleCategorySelectChange(e.target.value)}
                onBlur={handleBlur}
                className={inputClass}
              >
                <option value="">-- No category --</option>
                {categoryOptions.map((category) => (
                  <option key={`${category.nct_id}-${category.note_category}`} value={category.note_category}>
                    {category.note_category}
                  </option>
                ))}
              </select>
              <div className="update-note-field-hint">
                Provide Category or Note ID. If one is filled, the other is optional.
              </div>
              {isLoadingCategories && (
                <div className="update-note-field-hint">Loading categories from LP API...</div>
              )}
              {categoryLoadError && (
                <div className="update-note-field-hint">{categoryLoadError}</div>
              )}
            </div>
          )}

          {showAppendFields && (
            <>
              <div className="node-field">
                <label className="dark:text-white">Append By</label>
                <EnhancedVariableReference
                  value={nodeData.appendBy}
                  onChange={(value) => handleChange("appendBy", value)}
                  onBlur={handleBlur}
                  availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
                  placeholder="DME A/R Workflow"
                  className={inputClass}
                  showDateVariables={true}
                />
                <div className="update-note-field-hint">
                  Shown after &quot;by ...&quot; in the appended line. {VAR_HINT}
                </div>
              </div>

              <div className="node-field">
                <label className="dark:text-white">Employee ID (optional)</label>
                <EnhancedVariableReference
                  value={nodeData.empId}
                  onChange={(value) => handleChange("empId", value)}
                  onBlur={handleBlur}
                  availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
                  placeholder="{{empID}}"
                  className={inputClass}
                  showDateVariables={true}
                />
                <div className="update-note-field-hint">
                  Used to resolve the &quot;by&quot; name when Append By is blank. {VAR_HINT}
                </div>
              </div>
            </>
          )}

          <div className="node-field update-note-checkbox-row">
            <input
              id={`note-important-${id}`}
              type="checkbox"
              checked={Boolean(nodeData.noteImportant)}
              onChange={(e) => handleChange("noteImportant", e.target.checked, { persistOnChange: true })}
              className="nodrag"
            />
            <label
              htmlFor={`note-important-${id}`}
              className="dark:text-white"
              style={{ marginBottom: 0, cursor: "pointer" }}
            >
              Mark as important
            </label>
          </div>
        </div>
      )}

      <Handle type="target" position={Position.Top} id="b" />
      <Handle type="source" position={Position.Bottom} id="a" />
    </div>
  )
}

export default UpdateNoteNode
